import { useEffect, useState, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { TrialBanner } from "@/components/TrialBanner";
import { WhatsAppFeedbackButton } from "@/components/WhatsAppFeedbackButton";
import LandingPage from "@/pages/LandingPage";
import Onboarding from "@/pages/Onboarding";
import MyCourses from "@/pages/MyCourses";
import CountryLanding from "@/pages/CountryLanding";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
const ChampionshipPage = lazy(() => import("@/pages/ChampionshipPage"));
const Landing = lazy(() => import("@/pages/Landing"));
const FreeQuizPage = lazy(() => import("@/pages/FreeQuizPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const SpecialtyCatalog = lazy(() => import("@/pages/SpecialtyCatalog"));
const CourseCatalog = lazy(() => import("@/pages/CourseCatalog"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const GenerationProgress = lazy(() => import("@/pages/GenerationProgress"));
const UniversityCatalog = lazy(() => import("@/pages/UniversityCatalog"));
const JordanCatalog = lazy(() => import("@/pages/JordanCatalog"));
const PreviewAccess = lazy(() => import("@/pages/PreviewAccess"));
const VideoGallery = lazy(() => import("@/pages/VideoGallery"));
const StyleDemos = lazy(() => import("@/pages/StyleDemos"));
const CourseViewer = lazy(() => import("@/pages/CourseViewer"));
const StatsDashboard = lazy(() => import("@/pages/StatsDashboard"));
const LoginsReport = lazy(() => import("@/pages/LoginsReport"));
const AdminPaymentHealth = lazy(() => import("@/pages/AdminPaymentHealth"));
const AvatarPresentation = lazy(() => import("@/pages/AvatarPresentation"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const VideoManager = lazy(() => import("@/pages/VideoManager"));
const VideoLinkManager = lazy(() => import("@/pages/VideoLinkManager"));
const CoursesExport = lazy(() => import("@/pages/CoursesExport"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const MockExam = lazy(() => import("@/pages/MockExam"));
const MockExamLeaderboard = lazy(() => import("@/pages/MockExamLeaderboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdManager = lazy(() => import("@/pages/AdManager"));
const UniversityReport = lazy(() => import("@/pages/UniversityReport"));
const ContributionsAdmin = lazy(() => import("@/pages/ContributionsAdmin"));
const ContributionsSearch = lazy(() => import("@/pages/ContributionsSearch"));
const ErrorReportsAdmin = lazy(() => import("@/pages/ErrorReportsAdmin"));
const SupportEmailTemplate = lazy(() => import("@/pages/SupportEmailTemplate"));
const DbRestorePage = lazy(() => import("@/pages/DbRestorePage"));
const ScholarshipPage = lazy(() => import("@/pages/ScholarshipPage"));
const SrsPage = lazy(() => import("@/pages/SrsPage"));
const HallOfFame = lazy(() => import("@/pages/HallOfFame"));
const StudyRoomsList = lazy(() => import("@/pages/StudyRoomsList"));
const StudyRoom = lazy(() => import("@/pages/StudyRoom"));
const AdminCustomVideos = lazy(() => import("@/pages/AdminCustomVideos"));
const AdminVideoMappings = lazy(() => import("@/pages/AdminVideoMappings"));
const AdminMediaGovernance = lazy(() => import("@/pages/AdminMediaGovernance"));
const AdminMappingInbox = lazy(() => import("@/pages/AdminMappingInbox"));
const AdminPilotLive = lazy(() => import("@/pages/AdminPilotLive"));
const AdminPreviewList = lazy(() => import("@/pages/AdminPreviewList"));
const AdminPrompts = lazy(() => import("@/pages/AdminPrompts"));
const AdminCinematicDirector = lazy(() => import("@/pages/AdminCinematicDirector"));
const AdminStudioImport = lazy(() => import("@/pages/AdminStudioImport"));
const AdminRecovery = lazy(() => import("@/pages/AdminRecovery"));
const AdminQualityAudit = lazy(() => import("@/pages/AdminQualityAudit"));
const AdminCoverageDashboard = lazy(() => import("@/pages/AdminCoverageDashboard"));
const AdminContentRequests = lazy(() => import("@/pages/AdminContentRequests"));
const AdminV2CoverageHeatmap = lazy(() => import("@/pages/AdminV2CoverageHeatmap"));
const AdminUploadPropose = lazy(() => import("@/pages/AdminUploadPropose"));
const AdminBulkAddChapters = lazy(() => import("@/pages/AdminBulkAddChapters"));
const AdminDriveImport = lazy(() => import("@/pages/AdminDriveImport"));
const AdminReportsViewer = lazy(() => import("@/pages/AdminReportsViewer"));
const AdminDraftReview = lazy(() => import("@/pages/AdminDraftReview"));
const AdminSubscriptionCancel = lazy(() => import("@/pages/AdminSubscriptionCancel"));
const MistakeReviewPage = lazy(() => import("@/pages/MistakeReviewPage"));
const WeakTopicsHeatmapPage = lazy(() => import("@/pages/WeakTopicsHeatmapPage"));
const AmbassadorPage = lazy(() => import("@/pages/AmbassadorPage"));
const AdminTraffic = lazy(() => import("@/pages/AdminTraffic"));
import ChatWidget from "@/components/ChatWidget";
import { captureAttribution } from "@/lib/attribution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PageLoader() {
  return (
    <div className="page-loader" data-testid="page-loader">
      <div className="page-loader-spinner" />
    </div>
  );
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const pixelFirstLoad = useRef(true);

  useEffect(() => {
    if (pixelFirstLoad.current) {
      pixelFirstLoad.current = false; // initial PageView already fired by base code
      return;
    }
    if (window.fbq) window.fbq("track", "PageView");
  }, [location.pathname]);

  // CRITICAL: Detect session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <div key={location.pathname} className="page-transition">
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/my-courses" element={<MyCourses />} />
      <Route path="/country/:slug" element={<CountryLanding />} />
      <Route path="/free-quiz" element={<FreeQuizPage />} />
      <Route path="/championship" element={<ChampionshipPage />} />
      <Route path="/jordan" element={<JordanCatalog />} />
      <Route path="/preview-access" element={<PreviewAccess />} />
      <Route path="/home" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/my-progress" element={<DashboardPage />} />
      <Route path="/catalog/:majorId" element={<SpecialtyCatalog />} />
      <Route path="/major/:majorId" element={<CourseCatalog />} />
      <Route path="/university/:universityId" element={<UniversityCatalog />} />
      <Route path="/course/:courseId" element={<CourseDetail />} />
      <Route path="/videos" element={<VideoGallery />} />
      <Route path="/videos/:courseId" element={<CourseViewer />} />
      <Route path="/styles" element={<StyleDemos />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/ads" element={<AdManager />} />
      <Route path="/admin/university-report" element={<UniversityReport />} />
      <Route path="/admin/progress" element={<GenerationProgress />} />
      <Route path="/admin/stats" element={<StatsDashboard />} />
      <Route path="/admin/logins" element={<LoginsReport />} />
      <Route path="/admin/payment-health" element={<AdminPaymentHealth />} />
      <Route path="/presentation/:moduleId" element={<AvatarPresentation />} />
      {/* Subscription routes hidden - will enable after pilot group */}
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/subscription/success" element={<SubscriptionPage />} />
      <Route path="/subscription/cancel" element={<SubscriptionPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/help" element={<FAQPage />} />
      <Route path="/admin/videos" element={<VideoManager />} />
              <Route path="/admin/video-links" element={<VideoLinkManager />} />
      <Route path="/export" element={<CoursesExport />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/mock-exam" element={<MockExam />} />
      <Route path="/mock-exam/leaderboard" element={<MockExamLeaderboard />} />
      <Route path="/admin/contributions" element={<ContributionsAdmin />} />
      <Route path="/admin/error-reports" element={<ErrorReportsAdmin />} />
      <Route path="/admin/support-email" element={<SupportEmailTemplate />} />
      <Route path="/admin/db-restore" element={<DbRestorePage />} />
      <Route path="/scholarship" element={<ScholarshipPage />} />
      <Route path="/srs" element={<SrsPage />} />
      <Route path="/study-rooms" element={<StudyRoomsList />} />
      <Route path="/study-room/:roomId" element={<StudyRoom />} />
      <Route path="/admin/custom-videos" element={<AdminCustomVideos />} />
      <Route path="/admin/video-mappings" element={<AdminVideoMappings />} />
      <Route path="/admin/governance" element={<AdminMediaGovernance />} />
      <Route path="/admin/mapping-inbox" element={<AdminMappingInbox />} />
      <Route path="/admin/pilot" element={<AdminPilotLive />} />
      <Route path="/admin/preview-list" element={<AdminPreviewList />} />
      <Route path="/admin/prompts" element={<AdminPrompts />} />
      <Route path="/admin/cinematic" element={<AdminCinematicDirector />} />
      <Route path="/admin/studio-import" element={<AdminStudioImport />} />
      <Route path="/admin/recovery" element={<AdminRecovery />} />
      <Route path="/admin/quality" element={<AdminQualityAudit />} />
      <Route path="/admin/coverage" element={<AdminCoverageDashboard />} />
      <Route path="/admin/content-requests" element={<AdminContentRequests />} />
      <Route path="/admin/v2-coverage-heatmap" element={<AdminV2CoverageHeatmap />} />
      <Route path="/admin/upload-propose" element={<AdminUploadPropose />} />
      <Route path="/admin/bulk-add-chapters" element={<AdminBulkAddChapters />} />
      <Route path="/admin/drive-import" element={<AdminDriveImport />} />
      <Route path="/admin/reports" element={<AdminReportsViewer />} />
      <Route path="/admin/draft-review" element={<AdminDraftReview />} />
      <Route path="/admin/subscriptions/cancel" element={<AdminSubscriptionCancel />} />
      <Route path="/mistakes" element={<MistakeReviewPage />} />
      <Route path="/weak-topics" element={<WeakTopicsHeatmapPage />} />
      <Route path="/contributions/search" element={<ContributionsSearch />} />
      <Route path="/hall-of-fame" element={<HallOfFame />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/ambassador" element={<AmbassadorPage />} />
      <Route path="/admin/traffic" element={<AdminTraffic />} />
    </Routes>
      </div>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    captureAttribution();
  }, []);
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <TrialBanner />
            <AppRouter />
            <ChatWidget />
            <WhatsAppFeedbackButton />
            <Toaster position="top-right" richColors />
            <InstallPrompt />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
